import { DeliveryPreferenceType } from '@sg/graphql-schema';

import { addDeliveryAddressV2, radarAddressPredictionsQuery } from '../../queries';
import {
  formatRadarDeliveryAddressPrediction,
  getActiveCartDeliveryLocation,
  getCustomerDeliveryAddresses,
  getCustomerFirstDeliveryLocation,
  getLastInteractedStoreDeliveryLocation,
  getLastPlacedOrderDeliveryLocation,
} from './delivery-location.helpers';

import type { DeliveryLocationWithoutNearbyStore, Location } from '../../LocationSearch.types';
import type { RadarPredictions } from '@garnish/location';
import type { Client } from 'urql';

export { getDeliveryLocationDetailsByAddress } from './delivery-location.helpers';

// ─────────────────────────────────────────────────────────────────────────────

/**
 *  Fetches the default delivery location from the following sources and in the following order:
 *
 * - Delivery Location of the active card.
 * - Last interaction store delivery location.
 * - Last placed order delivery location.
 * - The customer's primary delivery address.
 */
export async function getDefaultDeliveryLocation(
  props: GetDefaultDeliveryLocationProps,
): Promise<Location | DeliveryLocationWithoutNearbyStore | undefined> {
  const { client, lastInteractedStoreAddressId = '' } = props;

  // ─── Active Card Delivery Location ───────────────────────────────────

  const activeCartDeliveryLocation = await getActiveCartDeliveryLocation({
    client,
  });

  if (activeCartDeliveryLocation) {
    return activeCartDeliveryLocation;
  }

  // ─── Last Interacted Store Delivery Location ─────────────────────────

  const lastInteractedStoreDeliveryLocation = await getLastInteractedStoreDeliveryLocation({
    client,
    lastInteractedStoreAddressId,
  });

  if (lastInteractedStoreDeliveryLocation) {
    return lastInteractedStoreDeliveryLocation;
  }

  // ─── Last Placed Order Delivery Location ─────────────────────────────

  const lastPlacedOrderDeliveryLocation = await getLastPlacedOrderDeliveryLocation({
    client,
  });

  if (lastPlacedOrderDeliveryLocation) {
    return lastPlacedOrderDeliveryLocation;
  }

  // ─── Customer Primary Delivery Location ──────────────────────────────

  const customerPrimaryDeliveryLocation = await getCustomerFirstDeliveryLocation({
    client,
  });

  if (customerPrimaryDeliveryLocation) {
    return customerPrimaryDeliveryLocation;
  }
}

/**
 * Fetches delivery addresses predictions using Radar APIs
 */
export async function getRadarDeliveryAddressPredictions(
  client: Client,
  searchString: string,
  near?: string,
) {
  const predictionsPromise = radarAddressPredictionsQuery(searchString, near);

  const customerDeliveryAddressesPromise = getCustomerDeliveryAddresses(client);

  const [predictionsResponse, customerDeliveryAddresses] = await Promise.all([
    predictionsPromise,
    customerDeliveryAddressesPromise,
  ]);

  const predictions = predictionsResponse.addresses as RadarPredictions;

  if (!predictions) {
    throw new Error('Failed to fetch delivery address predictions');
  }

  return predictions.map(formatRadarDeliveryAddressPrediction(customerDeliveryAddresses));
}

export async function addNewDeliveryAddressV2(
  client: Client,
  deliveryLocation: Location | DeliveryLocationWithoutNearbyStore | undefined,
) {
  if (!deliveryLocation || !deliveryLocation?.restaurantSlug) {
    throw new Error('Delivery location was not provided');
  }

  // Locations lacking a zip code are considered invalid and cannot be delivered to.
  if (!deliveryLocation?.zipCode) {
    throw new Error('Delivery location does not have a zip code');
  }

  const { city, country, state, street, zipCode } = deliveryLocation;

  if (!city || !country || !state || !street) {
    throw new Error('Delivery location is missing required address fields');
  }

  const { data } = await addDeliveryAddressV2(client, {
    input: {
      city,
      country,
      latitude: deliveryLocation.lat,
      longitude: deliveryLocation.lng,
      state,
      street,
      zipCode,
      deliveryPreference: deliveryLocation.deliveryPreference ?? DeliveryPreferenceType.LeaveAtDoor,
      name: deliveryLocation.name ?? '',
      secondaryStreet: deliveryLocation.secondaryStreet ?? '',
      notes: deliveryLocation.notes ?? '',
    },
  });

  const hasSuccessfullyAddedAddress = data?.addAddressV2.__typename === 'AddAddressSuccess';

  if (!hasSuccessfullyAddedAddress) {
    throw new Error('Failed to add new delivery address');
  }

  return data.addAddressV2.address.id;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type GetDefaultDeliveryLocationProps = Readonly<{
  client: Client;
  lastInteractedStoreAddressId?: string;

  /**
   * A flag to fetch extra upcharge disclosure fields from BE.
   */
}>;
